/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Module Pricing Hooks - React Query for SuperAdmin Pricing Management
 * ============================================================================
 *
 * Custom hooks for module pricing administration using React Query.
 * Provides CRUD operations for pricing tiers and volume discounts.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { modulePricingService } from '@/services/superadmin/module-pricing.service';
import {
  ModulePricingTier,
  VolumeDiscountRule,
  CreateModulePricingTierRequest,
  UpdateModulePricingTierRequest,
  CreateVolumeDiscountRequest,
  SubscriptionAnalytics,
} from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';

// Query keys for cache management
export const modulePricingKeys = {
  all: ['modulePricing'] as const,
  tiers: () => [...modulePricingKeys.all, 'tiers'] as const,
  tiersByModule: (moduleCode: string) => [...modulePricingKeys.tiers(), moduleCode] as const,
  tier: (moduleCode: string, tierCode: string) => [...modulePricingKeys.tiers(), moduleCode, tierCode] as const,
  moduleCodes: () => [...modulePricingKeys.all, 'moduleCodes'] as const,
  discounts: () => [...modulePricingKeys.all, 'discounts'] as const,
  analytics: () => [...modulePricingKeys.all, 'analytics'] as const,
  mrr: () => [...modulePricingKeys.all, 'mrr'] as const,
};

// ============================================================================
// Pricing Tiers
// ============================================================================

/**
 * Hook to fetch all pricing tiers.
 */
export function usePricingTiers(moduleCode?: string, options?: { enabled?: boolean }) {
  return useQuery<ModulePricingTier[], ApiError>({
    queryKey: moduleCode ? modulePricingKeys.tiersByModule(moduleCode) : modulePricingKeys.tiers(),
    queryFn: () => modulePricingService.getAllTiers(moduleCode),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled !== false,
  });
}

/**
 * Hook to fetch a specific pricing tier.
 */
export function usePricingTier(moduleCode: string, tierCode: string) {
  return useQuery<ModulePricingTier, ApiError>({
    queryKey: modulePricingKeys.tier(moduleCode, tierCode),
    queryFn: () => modulePricingService.getTier(moduleCode, tierCode),
    enabled: !!moduleCode && !!tierCode,
  });
}

/**
 * Hook for creating a pricing tier.
 */
export function useCreatePricingTier() {
  const queryClient = useQueryClient();

  return useMutation<ModulePricingTier, ApiError, CreateModulePricingTierRequest>({
    mutationFn: modulePricingService.createTier,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.tiers() });
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.tiersByModule(variables.moduleCode) });
    },
  });
}

/**
 * Hook for updating a pricing tier.
 */
export function useUpdatePricingTier() {
  const queryClient = useQueryClient();

  return useMutation<ModulePricingTier, ApiError, { id: string; data: UpdateModulePricingTierRequest }>({
    mutationFn: ({ id, data }) => modulePricingService.updateTier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.tiers() });
    },
  });
}

/**
 * Hook for activating a pricing tier.
 */
export function useActivatePricingTier() {
  const queryClient = useQueryClient();

  return useMutation<ModulePricingTier, ApiError, string>({
    mutationFn: modulePricingService.activateTier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.tiers() });
    },
  });
}

/**
 * Hook for deactivating a pricing tier.
 */
export function useDeactivatePricingTier() {
  const queryClient = useQueryClient();

  return useMutation<ModulePricingTier, ApiError, string>({
    mutationFn: modulePricingService.deactivateTier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.tiers() });
    },
  });
}

/**
 * Hook for deleting a pricing tier.
 */
export function useDeletePricingTier() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: modulePricingService.deleteTier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.tiers() });
    },
  });
}

/**
 * Hook to fetch all module codes for dropdown selection.
 */
export function useModuleCodes() {
  return useQuery<string[], ApiError>({
    queryKey: modulePricingKeys.moduleCodes(),
    queryFn: () => modulePricingService.getModuleCodes(),
    staleTime: 10 * 60 * 1000, // 10 minutes - module codes rarely change
  });
}

// ============================================================================
// Volume Discounts
// ============================================================================

/**
 * Hook to fetch all volume discount rules.
 */
export function useVolumeDiscounts(options?: { enabled?: boolean }) {
  return useQuery<VolumeDiscountRule[], ApiError>({
    queryKey: modulePricingKeys.discounts(),
    queryFn: () => modulePricingService.getDiscountRules(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  });
}

/**
 * Hook for creating a volume discount rule.
 */
export function useCreateVolumeDiscount() {
  const queryClient = useQueryClient();

  return useMutation<VolumeDiscountRule, ApiError, CreateVolumeDiscountRequest>({
    mutationFn: modulePricingService.createDiscountRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.discounts() });
    },
  });
}

/**
 * Hook for updating a volume discount rule.
 */
export function useUpdateVolumeDiscount() {
  const queryClient = useQueryClient();

  return useMutation<VolumeDiscountRule, ApiError, { id: string; data: CreateVolumeDiscountRequest }>({
    mutationFn: ({ id, data }) => modulePricingService.updateDiscountRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.discounts() });
    },
  });
}

/**
 * Hook for deleting a volume discount rule.
 */
export function useDeleteVolumeDiscount() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: modulePricingService.deleteDiscountRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modulePricingKeys.discounts() });
    },
  });
}

// ============================================================================
// Analytics
// ============================================================================

/**
 * Hook to fetch subscription analytics.
 */
export function useSubscriptionAnalytics() {
  return useQuery<SubscriptionAnalytics, ApiError>({
    queryKey: modulePricingKeys.analytics(),
    queryFn: () => modulePricingService.getAnalytics(),
    staleTime: 2 * 60 * 1000, // 2 minutes - analytics should be fresh
  });
}

/**
 * Hook to fetch total MRR.
 */
export function useTotalMrr() {
  return useQuery<number, ApiError>({
    queryKey: modulePricingKeys.mrr(),
    queryFn: () => modulePricingService.getTotalMrr(),
    staleTime: 2 * 60 * 1000,
  });
}
